import { useState, useEffect, useRef } from 'react';
import { Sun, Clock, Moon, AlertTriangle, ArrowLeft, ShieldAlert, Package, BadgeDollarSign, Stethoscope, ShoppingCart, Loader2, Mail, X, Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Protocol, SupplementRecommendation, ProtocolTiming, BiomarkerResult } from '../../types';
import { startCheckout } from '../../services/checkout/checkoutService';
import { stripProtocol, StrippingError } from '../../services/protocol/protocolStrippingService';
import { CreateAccountPrompt } from '../patient/CreateAccountPrompt';
import { getCurrentPatientAuth } from '../../services/auth/authUpgradeService';
import { exportProtocolPdf } from '../../services/pdfExport';
import { trackFunnelEvent } from '../../services/analytics/funnelTracker';
import { MedicalDisclaimer } from '../shared/MedicalDisclaimer';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';

interface ProtocolViewProps {
  protocol: Protocol;
  onBack?: () => void;
  /** When true, renders without its own header (for embedding in a modal) */
  embedded?: boolean;
  /** Customer email for checkout */
  customerEmail?: string;
  /** When provided, navigates to checkout review instead of direct Stripe redirect */
  onCheckout?: (email: string) => void;
  /** Patient age for PDF export (HIPAA-safe: no name/email) */
  patientAge?: number;
  /** Patient sex for PDF export */
  patientSex?: 'female' | 'male';
  /** Biomarker results for PDF export */
  biomarkers?: BiomarkerResult[];
  /** Gate level: 1 = OTC supplements checkout, 2 = provider review required */
  gateLevel?: 1 | 2;
}

// CLINICAL SAFETY: Refuse to display fabricated protocol data on production hostnames.
function isMockOnProduction(protocol: Protocol): boolean {
  if (!protocol.isMockData) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host === 'hemologix.com' || host === 'www.hemologix.com') return true;
  if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) return true;
  return false;
}

const TIMING_CONFIG: Record<ProtocolTiming, { label: string; icon: typeof Sun; description: string }> = {
  AM_ENERGY: { label: 'Morning', icon: Sun, description: 'Take with breakfast' },
  MIDDAY_ABSORPTION: { label: 'Midday', icon: Clock, description: 'Take between meals' },
  PM_RECOVERY: { label: 'Evening', icon: Moon, description: 'Take before bed' },
};

const EVIDENCE_COLORS: Record<string, string> = {
  strong: 'bg-optimal/20 text-optimal',
  moderate: 'bg-suboptimal/20 text-suboptimal',
  emerging: 'bg-text-muted/20 text-text-muted',
};

function SmartPackCard({ supplement, index }: { supplement: SupplementRecommendation; index: number }) {
  return (
    <div
      className="bg-bg-card border border-bg-tertiary rounded-xl p-4 animate-fade-in-up"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-text-primary">{supplement.name}</h4>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${EVIDENCE_COLORS[supplement.evidenceLevel]}`}>
          {supplement.evidenceLevel}
        </span>
      </div>

      <p className="text-accent-light text-sm font-medium mb-2">{supplement.dosage}</p>

      <p className="text-text-secondary text-xs leading-relaxed mb-3">{supplement.rationale}</p>

      {supplement.interactionWarnings.length > 0 && (
        <div className="flex items-start gap-1.5 mb-2">
          <AlertTriangle size={12} className="text-suboptimal mt-0.5 flex-shrink-0" />
          <p className="text-suboptimal text-[10px]">{supplement.interactionWarnings[0]}</p>
        </div>
      )}

      {supplement.requiresRx && (
        <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 rounded-lg bg-amber-900/15 border border-amber-500/20">
          <Stethoscope size={12} className="text-amber-400 flex-shrink-0" />
          <p className="text-amber-400 text-[11px] font-medium">Rx — Discuss with your provider</p>
        </div>
      )}

      {supplement.gender !== 'both' && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-tertiary text-text-muted">
          {supplement.gender === 'M' ? 'Male' : 'Female'} specific
        </span>
      )}
    </div>
  );
}

export function ProtocolView({ protocol, onBack, embedded, customerEmail, onCheckout, patientAge, patientSex, biomarkers, gateLevel = 1 }: ProtocolViewProps) {
  const isGate2 = gateLevel === 2 || protocol.supplements.some(s => s.requiresRx);
  // CLINICAL SAFETY: Block mock protocol data on production
  if (isMockOnProduction(protocol)) {
    return (
      <div className="p-6 text-center">
        <p className="text-low text-sm font-medium mb-2">Unable to display protocol</p>
        <p className="text-text-secondary text-xs">A system error prevented your personalized protocol from loading. Please try again or contact support.</p>
        {onBack && (
          <button onClick={onBack} className="mt-3 text-xs text-accent-light underline">Go back</button>
        )}
      </div>
    );
  }

  const timings: ProtocolTiming[] = ['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'];
  const suboptimalCount = protocol.supplements.length;
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Track protocol view on mount (once)
  const trackedRef = useRef(false);
  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      trackFunnelEvent({
        event: 'PROTOCOL_VIEWED',
        metadata: { supplementCount: protocol.supplements.length, bundleName: protocol.bundleName || '' },
      });
    }
  }, [protocol.supplements.length, protocol.bundleName]);

  // Auto-detect email from Firebase Auth if patient is authenticated
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  useEffect(() => {
    const auth = getCurrentPatientAuth();
    if (auth.isAuthenticated && auth.email) {
      setAuthEmail(auth.email);
    }
  }, []);

  // Resolved email: props > auth > manual input
  const resolvedEmail = customerEmail || authEmail;

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleCheckout = async () => {
    if (!protocol.bundlePrice) return;

    trackFunnelEvent({
      event: 'CHECKOUT_STARTED',
      metadata: { bundlePrice: protocol.bundlePrice, bundleName: protocol.bundleName || '' },
    });

    // If we already have an email (from props or auth), go straight to checkout
    if (resolvedEmail) {
      await proceedToCheckout(resolvedEmail);
      return;
    }

    // Otherwise show the inline email form
    setShowEmailForm(true);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const trimmed = emailInput.trim();
    if (!trimmed) {
      setEmailError('Email is required');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    await proceedToCheckout(trimmed);
  };

  const proceedToCheckout = async (email: string) => {
    // If onCheckout callback is provided, navigate to checkout review screen
    if (onCheckout) {
      onCheckout(email);
      return;
    }

    // PRO.10: Strip clinical data before checkout
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      // Build product catalog for quantity derivation
      const allProducts = [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY];
      const productCatalog = new Map<string, { skuUnitSize?: string; typicalQuantity?: number; defaultDurationDays?: number }>();
      for (const p of allProducts) {
        const dosageMatch = p.dosage.match(/^(\d+)\s*(\w+)/);
        if (dosageMatch) {
          productCatalog.set(p.id, {
            skuUnitSize: `1 ${dosageMatch[2]}`,
            typicalQuantity: parseInt(dosageMatch[1], 10),
            defaultDurationDays: 90,
          });
        } else {
          productCatalog.set(p.id, { typicalQuantity: 1, defaultDurationDays: 90 });
        }
      }

      // Strip protocol — remove clinical data before it hits Stripe
      const gate = isGate2 ? 'GATE_2' as const : 'GATE_1' as const;
      await stripProtocol({
        protocolId: protocol.id,
        supplements: protocol.supplements.map(s => ({
          productId: s.productId ?? s.name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
          name: s.name,
          dosage: s.dosage,
          timing: s.timing,
          rationale: s.rationale,
        })),
        gate,
        approvalStatus: gate === 'GATE_1' ? 'NOT_REQUIRED' : 'PENDING',
        consentTimestamps: [new Date().toISOString()],
        productCatalog,
        durationString: '90 days',
      });

      // Stripping passed — proceed to Stripe
      const { sessionUrl } = await startCheckout(protocol, email);
      if (sessionUrl) {
        window.location.href = sessionUrl;
      }
    } catch (err) {
      if (err instanceof StrippingError && err.code === 'GATE_2_NOT_APPROVED') {
        // Expected: Gate 2 protocol requires provider approval before checkout
        setCheckoutError('This protocol requires provider review before purchase. Please connect with your provider.');
      } else {
        setCheckoutError(err instanceof Error ? err.message : 'Checkout failed');
      }
      setCheckoutLoading(false);
    }
  };

  return (
    <div className={embedded ? 'text-text-primary' : 'min-h-screen bg-bg-deep text-text-primary'}>
      {/* Header — only shown in standalone mode */}
      {!embedded && (
        <div className="sticky top-0 z-10 bg-bg-deep/95 backdrop-blur border-b border-bg-tertiary px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button onClick={onBack} className="text-text-muted hover:text-text-primary transition-colors">
                  <ArrowLeft size={20} />
                </button>
              )}
              <div>
                <h2 className="font-display text-lg font-bold">Your Protocol</h2>
                <p className="text-text-muted text-xs">{suboptimalCount} targeted supplements</p>
              </div>
            </div>
            {patientAge && patientSex && (
              <button
                onClick={() => { trackFunnelEvent({ event: 'PDF_DOWNLOADED' }); exportProtocolPdf(protocol, { age: patientAge, sex: patientSex, biomarkers }); }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-bg-tertiary text-text-secondary hover:text-accent-light hover:border-accent-light/40 transition-colors"
                title="Download protocol as PDF"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Download PDF</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className={embedded ? 'max-w-lg mx-auto overflow-hidden' : 'px-4 py-6 max-w-lg mx-auto overflow-hidden'}>
        {/* Brief disclaimer at top */}
        <div className="px-3 py-2 mb-4 rounded-lg bg-bg-elevated/50 border border-bg-tertiary">
          <p className="text-text-muted text-xs leading-relaxed italic">
            This is a wellness optimization protocol, not a medical prescription. Always consult your healthcare provider before starting any supplement regimen.
          </p>
        </div>

        {/* Summary */}
        <div
          className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-6 animate-fade-in-up"
          style={{ animationDelay: '100ms' }}
        >
          <p className="text-sm text-text-secondary leading-relaxed">{protocol.summary}</p>
          {protocol.tierRecommendation > 1 && protocol.tierRecommendation < 4 && (
            <div className="mt-3 flex items-center gap-2 text-gold text-xs">
              <span className="w-2 h-2 rounded-full bg-gold" />
              Advanced optimization options may be available — ask your nurse.
            </div>
          )}
        </div>

        {/* Safety Warnings */}
        {protocol.safetyWarnings && protocol.safetyWarnings.length > 0 && (
          <div
            className="bg-critical/10 border border-critical/30 rounded-xl p-4 mb-4 animate-fade-in-up"
            style={{ animationDelay: '150ms' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={16} className="text-critical" />
              <h3 className="text-sm font-bold text-critical">Safety Alerts</h3>
            </div>
            {protocol.safetyWarnings.map((w, i) => (
              <p key={i} className="text-text-secondary text-xs leading-relaxed mb-1">{w}</p>
            ))}
          </div>
        )}

        {/* Timing sections */}
        {timings.map((timing) => {
          const supplements = protocol.supplements.filter((s) => s.timing === timing);
          if (supplements.length === 0) return null;

          const config = TIMING_CONFIG[timing];
          const Icon = config.icon;

          return (
            <div key={timing} className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-gold" />
                <h3 className="font-display text-base font-bold">{config.label}</h3>
                <span className="text-text-muted text-xs">— {config.description}</span>
              </div>
              <div className="flex flex-col gap-3">
                {supplements.map((s, i) => (
                  <SmartPackCard key={s.name} supplement={s} index={i} />
                ))}
              </div>
            </div>
          );
        })}

        {/* Account creation prompt — shown after protocol, before checkout */}
        <CreateAccountPrompt />

        {/* Bundle + Price — at bottom above disclaimers */}
        {protocol.bundleName && (
          <div
            className="bg-gradient-to-br from-accent/10 to-gold/10 border border-accent/30 rounded-xl p-4 mb-4 animate-fade-in-up"
          >
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} className="text-gold" />
              <h3 className="text-sm font-bold text-gold">{protocol.bundleName}</h3>
            </div>
            <p className="text-text-muted text-xs mb-2">
              {protocol.supplements.length} targeted products • organized by timing for optimal absorption
            </p>
            {protocol.bundlePrice && (
              <div className="pt-2 border-t border-accent/20">
                <div className="flex items-center gap-2 mb-3">
                  <BadgeDollarSign size={16} className="text-accent-light" />
                  <p className="text-accent-light text-base font-bold">{protocol.bundlePrice}</p>
                </div>
                {/* Inline email form — shown when no email (prop or auth) and user clicks checkout */}
                {showEmailForm && !resolvedEmail && (
                  <form onSubmit={handleEmailSubmit} className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="checkout-email" className="text-text-secondary text-xs font-medium">
                        Email for order confirmation
                      </label>
                      <button
                        type="button"
                        onClick={() => { setShowEmailForm(false); setEmailError(null); }}
                        className="text-text-muted hover:text-text-primary transition-colors"
                        aria-label="Close email form"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                        <input
                          id="checkout-email"
                          type="email"
                          autoFocus
                          placeholder="you@example.com"
                          value={emailInput}
                          onChange={(e) => { setEmailInput(e.target.value); setEmailError(null); }}
                          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-bg-tertiary bg-bg-card text-text-primary placeholder:text-text-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={checkoutLoading}
                        className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-accent-light text-bg-deep font-bold rounded-lg hover:bg-accent-light/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                      >
                        {checkoutLoading ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <>
                            <ShoppingCart size={16} />
                            Checkout
                          </>
                        )}
                      </button>
                    </div>
                    {emailError && (
                      <p className="text-critical text-xs mt-1.5">{emailError}</p>
                    )}
                    <p className="text-text-muted text-[11px] mt-1.5">
                      {onCheckout
                        ? 'You will review your order and add shipping on the next page.'
                        : 'Shipping address will be collected on the next page.'}
                    </p>
                  </form>
                )}

                {/* Primary action button — Gate 1: checkout, Gate 2: connect with provider */}
                {!showEmailForm && !isGate2 && (
                  <button
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-light text-bg-deep font-bold rounded-xl hover:bg-accent-light/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart size={18} />
                        Get Your Protocol
                      </>
                    )}
                  </button>
                )}
                {!showEmailForm && isGate2 && (
                  <div>
                    <div className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/20 text-amber-400 font-bold rounded-xl border border-amber-500/30">
                      <Stethoscope size={18} />
                      Connect with Provider
                    </div>
                    <p className="text-text-muted text-xs mt-2 text-center">
                      This protocol includes items that require provider review. A licensed provider will review your results and approve your protocol.
                    </p>
                  </div>
                )}
                {checkoutError && (
                  <p className="text-critical text-xs mt-2 text-center">{checkoutError}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Download PDF — shown when patient context is available */}
        {patientAge && patientSex && (
          <div className="mt-4 mb-4">
            <button
              onClick={() => { trackFunnelEvent({ event: 'PDF_DOWNLOADED' }); exportProtocolPdf(protocol, { age: patientAge, sex: patientSex, biomarkers }); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-bg-tertiary text-text-secondary hover:text-accent-light hover:border-accent-light/40 transition-colors"
            >
              <Download size={16} />
              Download Protocol as PDF
            </button>
          </div>
        )}

        {/* Full DSHEA Disclaimers — very bottom */}
        {protocol.warnings.length > 0 && (
          <div
            className="mt-4 p-3 rounded-lg bg-bg-elevated border border-bg-tertiary animate-fade-in"
            style={{ animationDelay: '500ms' }}
          >
            {protocol.warnings.map((w, i) => (
              <p key={i} className="text-text-muted text-[10px] leading-relaxed">{w}</p>
            ))}
          </div>
        )}

        {/* Medical Disclaimer — Launch Blocker #12 */}
        <MedicalDisclaimer className="mt-4" />

        {/* Adverse Reaction Support — Launch Blocker #13 */}
        <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert size={15} className="text-red-700 flex-shrink-0" />
            <p className="text-[13px] font-semibold text-red-900">Experiencing Side Effects?</p>
          </div>
          <ol className="text-[13px] text-zinc-700 leading-relaxed space-y-1 list-decimal list-inside mb-3">
            <li><strong className="text-zinc-900">Stop taking the supplement</strong> immediately</li>
            <li><strong className="text-zinc-900">Contact your healthcare provider</strong> if symptoms are concerning</li>
            <li><strong className="text-zinc-900">Report it to us</strong> so we can update your profile</li>
          </ol>
          <div className="flex flex-col gap-1.5">
            <Link
              to="/support#adverse-reaction"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rose-600 hover:underline"
            >
              <ShieldAlert size={13} />
              Report an adverse reaction
            </Link>
            <a
              href="mailto:safety@hemologix.ai"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-700 hover:underline"
            >
              Or email safety@hemologix.ai directly
            </a>
            <a
              href="https://www.safetyreporting.hhs.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-zinc-400 hover:text-zinc-600 hover:underline"
            >
              <ExternalLink size={11} />
              File with FDA MedWatch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
